import type { SettingsRow } from '@/types'

import { useEffect, useState } from 'react'
import { PartyPopper, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { BIRTHDAY } from '@/constants/site'

type BirthdayPhase =
    | { phase: 'before', days: number, hours: number, minutes: number, seconds: number }
    | { phase: 'today' }
    | { phase: 'after' }

const getPhase = (now: Date): BirthdayPhase => {
    const year = now.getFullYear()
    const start = new Date(year, BIRTHDAY.month - 1, BIRTHDAY.day)
    const end = new Date(year, BIRTHDAY.month - 1, BIRTHDAY.day + 1)

    if (now >= start && now < end) return { phase: 'today' }
    if (now >= end) return { phase: 'after' }

    const total = Math.max(0, Math.floor((start.getTime() - now.getTime()) / 1000))

    return {
        phase: 'before',
        days: Math.floor(total / 86400),
        hours: Math.floor((total % 86400) / 3600),
        minutes: Math.floor((total % 3600) / 60),
        seconds: total % 60
    }
}

const pad = (value: number): string => String(value).padStart(2, '0')

interface CountdownProps {
    settings: SettingsRow
    className?: string
}

/**
 * Counts down to her birthday in the visitor's own time zone, and switches to
 * the "today" message on the day itself. Rendered only after mount, because
 * the server cannot know the visitor's clock.
 */
export default function Countdown({ settings, className }: CountdownProps) {
    const [state, setState] = useState<BirthdayPhase | null>(null)

    useEffect(() => {
        const tick = () => setState(getPhase(new Date()))

        tick()

        const timer = window.setInterval(tick, 1000)

        return () => window.clearInterval(timer)
    }, [])

    const units = state?.phase === 'before'
        ? [
            { label: 'Din', value: state.days },
            { label: 'Ghante', value: state.hours },
            { label: 'Minute', value: state.minutes },
            { label: 'Second', value: state.seconds }
        ]
        : []

    return (
        <div className={cn('min-h-36 w-full max-w-md rounded-3xl bg-white/[0.07] p-5 ring-1 ring-white/15 backdrop-blur-md sm:p-6', className)}>
            {!state && <div aria-hidden className='h-26' />}

            {state?.phase === 'before' && (
                <>
                    {settings.countdown_label && (
                        <p
                            data-mdlfy-cms={`settings:${settings.id}:countdown_label`}
                            className='text-center text-sm font-semibold text-cream/80'
                        >
                            {settings.countdown_label}
                        </p>
                    )}

                    <div role='timer' aria-live='off' className='mt-4 grid grid-cols-4 gap-2 sm:gap-3'>
                        {units.map(unit => (
                            <div key={unit.label} className='rounded-2xl bg-night/50 px-1 py-3 text-center ring-1 ring-white/10'>
                                <span className='block font-heading text-3xl font-semibold text-cream tabular-nums sm:text-4xl'>
                                    {pad(unit.value)}
                                </span>

                                <span className='mt-1 block text-[0.68rem] font-bold tracking-[0.18em] text-[#ff9ab4] uppercase'>
                                    {unit.label}
                                </span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {state?.phase === 'today' && settings.today_message && (
                <div className='flex flex-col items-center gap-3 py-2 text-center'>
                    <PartyPopper aria-hidden className='size-8 text-gold' />

                    <p
                        data-mdlfy-cms={`settings:${settings.id}:today_message`}
                        className='font-heading text-xl leading-snug text-balance text-cream italic sm:text-2xl'
                    >
                        {settings.today_message}
                    </p>
                </div>
            )}

            {state?.phase === 'after' && settings.after_message && (
                <div className='flex flex-col items-center gap-3 py-2 text-center'>
                    <Sparkles aria-hidden className='size-8 text-gold' />

                    <p
                        data-mdlfy-cms={`settings:${settings.id}:after_message`}
                        className='font-heading text-xl leading-snug text-balance text-cream italic sm:text-2xl'
                    >
                        {settings.after_message}
                    </p>
                </div>
            )}
        </div>
    )
}
